'use client';
import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import {
  type ComponentPropsWithoutRef,
  cloneElement,
  type ElementRef,
  forwardRef,
  isValidElement,
  type ReactNode,
} from 'react';

import { Spinner } from '@air/primitive-spinner';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const button = tailwindVariants({
  base: 'relative flex shrink-0 items-center justify-center font-semibold transition-colors hover:no-underline disabled:cursor-not-allowed disabled:bg-grey-3 disabled:text-grey-12',
  compoundVariants: [
    {
      appearance: 'filled',
      class: 'bg-blue-9 text-white hover:bg-blue-10 hover:text-white active:bg-blue-10',
      color: 'blue',
    },
    {
      appearance: 'filled',
      class: 'bg-violet-9 text-white hover:bg-violet-10 hover:text-white active:bg-violet-10',
      color: 'violet',
    },
    {
      appearance: 'filled',
      class: 'border border-grey-5 bg-grey-3 text-grey-11 active:bg-grey-4 hover:bg-grey-5',
      color: 'grey',
    },
    {
      appearance: 'filled',
      class: 'bg-red-9 text-white hover:bg-red-10 hover:text-white active:bg-red-10',
      color: 'red',
    },
    {
      appearance: 'ghost',
      class: 'text-blue-11 hover:bg-blue-1 active:bg-blue-1',
      color: 'blue',
    },
    {
      appearance: 'ghost',
      class: 'text-violet-11 hover:bg-violet-1 active:bg-violet-1',
      color: 'violet',
    },
    {
      appearance: 'ghost',
      class: 'text-grey-11 active:bg-grey-4 hover:bg-grey-5',
      color: 'grey',
    },
    {
      appearance: 'ghost',
      class: 'active:red-2 text-red-11 hover:bg-red-2',
      color: 'red',
    },
    {
      appearance: 'outline',
      class: 'border-blue-11 text-blue-11 hover:bg-blue-1 active:bg-blue-1',
      color: 'blue',
    },
    {
      appearance: 'outline',
      class: 'border-violet-5 text-violet-12 hover:bg-violet-1 active:bg-violet-1',
      color: 'violet',
    },
    {
      appearance: 'outline',
      class: 'border-grey-5 text-grey-11 active:bg-grey-4 hover:bg-grey-5',
      color: 'grey',
    },
    {
      appearance: 'outline',
      class: 'border-red-11 text-red-11 hover:bg-red-2 active:bg-red-2',
      color: 'red',
    },
  ],
  defaultVariants: {
    appearance: 'filled',
    color: 'blue',
    size: 'medium',
  },
  variants: {
    appearance: {
      filled: 'border-0',
      ghost: 'border-0 bg-transparent disabled:bg-transparent disabled:text-grey-10',
      outline: 'border bg-transparent disabled:border-0',
    },
    color: {
      blue: '',
      grey: '',
      red: '',
      violet: '',
    },
    size: {
      'extra-large': 'h-12 px-4 text-16 rounded-8',
      'extra-small': 'h-4 px-2 text-10 rounded-4',
      large: 'h-10 px-3 text-14 rounded-8',
      medium: 'h-8 px-2.5 text-14 rounded-8',
      small: 'h-6 px-2 text-12 rounded-4',
    },
  },
});

export const buttonSpinner = tailwindVariants({
  defaultVariants: {
    size: 'medium',
  },
  variants: {
    size: {
      'extra-large': 'h-5 w-5',
      'extra-small': 'h-2 w-2',
      large: 'h-5 w-5',
      medium: 'h-4 w-4',
      small: 'h-3 w-3',
    },
  },
});

export type ButtonVariants = VariantProps<typeof button>;

export type ButtonSpinnerVariants = VariantProps<typeof buttonSpinner>;

export const BUTTON_ELEMENT_TAG = 'button';

export type ButtonProps = Omit<ComponentPropsWithoutRef<typeof BUTTON_ELEMENT_TAG>, 'prefix' | 'suffix'> &
  ButtonVariants & {
    asChild?: boolean;
    isLoading?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
  };

export const Button = forwardRef<ElementRef<typeof BUTTON_ELEMENT_TAG>, ButtonProps>(
  (
    {
      appearance,
      asChild,
      children,
      className,
      color,
      disabled,
      isLoading,
      prefix,
      size,
      suffix,
      type = 'button',
      ...restOfProps
    },
    forwardedRef,
  ) => {
    // biome-ignore lint/suspicious/noExplicitAny: <https://linear.app/air/issue/ENG-132/update-button-to-use-primitive-and-remove-prefix-and-suffix-so-we-dont>
    const Component: any = asChild ? Slot : BUTTON_ELEMENT_TAG;
    const isSmall = !!size && ['extra-small', 'small'].includes(size);
    const childrenNode =
      // biome-ignore lint/suspicious/noExplicitAny: <https://linear.app/air/issue/ENG-132/update-button-to-use-primitive-and-remove-prefix-and-suffix-so-we-dont>
      asChild && isValidElement<any>(children) ? (
        cloneElement(children, {
          children: (
            <>
              {isLoading ? (
                // biome-ignore lint/a11y/useSemanticElements: <This is to indicate a loading state, we pair it w/ aria-live and role="status">
                <div aria-live="polite" className={classNames(isSmall ? 'mr-1' : 'mr-2')} role="status">
                  <VisuallyHidden>Loading...</VisuallyHidden>
                  <Spinner className={buttonSpinner({ size })} />
                </div>
              ) : prefix ? (
                <div className={classNames(isSmall ? 'mr-1 shrink-0' : 'mr-2 shrink-0')}>{prefix}</div>
              ) : null}
              {children.props.children}
              {suffix ? <div className={classNames(isSmall ? 'ml-1 shrink-0' : 'ml-2 shrink-0')}>{suffix}</div> : null}
            </>
          ),
        })
      ) : (
        <>
          {isLoading ? (
            // biome-ignore lint/a11y/useSemanticElements: <This is to indicate a loading state, we pair it w/ aria-live and role="status">
            <div aria-live="polite" className={classNames(isSmall ? 'mr-1' : 'mr-2')} role="status">
              <VisuallyHidden>Loading...</VisuallyHidden>
              <Spinner aria-hidden className={buttonSpinner({ size })} />
            </div>
          ) : prefix ? (
            <div className={classNames(isSmall ? 'mr-1 shrink-0' : 'mr-2 shrink-0')}>{prefix}</div>
          ) : null}
          {children}
          {suffix ? <div className={classNames(isSmall ? 'ml-1 shrink-0' : 'ml-2 shrink-0')}>{suffix}</div> : null}
        </>
      );

    return (
      <Component
        className={classNames(
          button({ appearance, class: className, color, size }),
          isLoading || disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
        disabled={disabled}
        ref={forwardedRef}
        type={type}
        {...restOfProps}
      >
        {childrenNode}
      </Component>
    );
  },
);

Button.displayName = 'Button';
